import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Tooltip } from '@material-ui/core';
import PlaceIcon from '@material-ui/icons/Place';
import AssignmentLateIcon from '@material-ui/icons/AssignmentLate';
import VisibilityIcon from '@material-ui/icons/Visibility';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { RootState } from '../../../../../redux/root';
import { CustomParams, PermitInspectionsType } from '../../../types';
import EditInspectionPopup from './EditInspectionPopup';
import { setSelectedInspection } from '../../../../../redux/slices/inspectionsSlice';
import { sortTableHeader } from '../../../utils';
import { setBottomDrawerOpened, setTableSortingDirection, setTableSortingKey } from '../../../../../redux/slices/generalSlice';
import { InspectionStatuses } from '../../../../../constants';
import { useGetPermitConditionsQuery } from '../../../../../redux/rtkQuery/apiSlice';
import { Loader } from '../../../../../atoms';
import BulkRequestInspectionsPopup from './BulkRequestInspectionsPopup';

type VisibleInspectionType = {
  id: number;
  code: string;
  description: string;
  inspectionDate: string;
  statusName: string;
  report: string | null;
  inspectorName: string;
};
export type ScreenType = 'Request' | 'View';

type StatusTone = 'success' | 'warning' | 'info' | 'neutral' | 'danger';

const toneByStatus = (raw: string): StatusTone => {
  const s = (raw || '').trim().toLowerCase();
  if (!s) return 'neutral';
  if (s.includes('pass') || s === 'approved') return 'success';
  if (s.includes('fail') || s.includes('rejected') || s.includes('cancelled')) return 'danger';
  if (s.includes('not applicable')) return 'neutral';
  if (s.includes('scheduled') || s.includes('threshold') || s.includes('inspector')) return 'info';
  return 'warning';
};

const tonePalette: Record<StatusTone, { bg: string; fg: string; border: string }> = {
  success: { bg: '#E8F6EE', fg: '#196A3B', border: '#C5E7D2' },
  warning: { bg: '#FFF6E0', fg: '#8A5F05', border: '#FFE7AC' },
  info: { bg: '#E8F2FD', fg: '#1E64BF', border: '#CFE6F3' },
  danger: { bg: '#FDECEC', fg: '#9A2523', border: '#F9C9C9' },
  neutral: { bg: '#F2F4F7', fg: '#3B424E', border: '#E6E9EF' },
};

// Inspection dates arrive as MM/DD/YYYY (or ISO YYYY-MM-DD from some payloads).
const isToday = (raw: string): boolean => {
  if (!raw) return false;
  const us = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  let y: number;
  let m: number;
  let d: number;
  if (us) {
    [m, d, y] = [Number(us[1]), Number(us[2]), Number(us[3])];
  } else if (iso) {
    [y, m, d] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
  } else {
    return false;
  }
  const now = new Date();
  return now.getFullYear() === y && now.getMonth() + 1 === m && now.getDate() === d;
};

const PermitInspectionsDetails = () => {
  const selectedPermit: PermitInspectionsType = useSelector((s: RootState) => s.inspections.selectedPermit);
  const [inspections, setInspections] = useState<VisibleInspectionType[]>([]);
  const [updatedStatusId, setUpdatedStatusId] = useState<number>(-1);
  const [params, setParams] = useState(null);
  const [disableFromHold, setDisableFromHold] = useState<boolean>(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkOpen, setBulkOpen] = useState<boolean>(false);
  // Snapshot of the inspections sent to the bulk popup, so its success view
  // survives the parent clearing the row selection.
  const [bulkTargets, setBulkTargets] = useState<any[]>([]);
  const {
    data: conditions,
    isFetching: conditionsFetching,
  } = useGetPermitConditionsQuery(params, { skip: params === null });
  const { tableSortingDirection, tableSortingKey } = useSelector((s: RootState) => s.general);
  const dispatch = useDispatch();

  useEffect(() => {
    if (selectedPermit.inspections) {
      const visibleInspections = getVisibleInspections();
      setInspections(visibleInspections);

      if (selectedPermit.status === 'Hold') {
        setDisableFromHold(true);
      } else {
        const _params: CustomParams = {
          is_deleted: false,
          limit: 100,
          offset: 0,
          permit_id: selectedPermit.permitId,
          event_type: 'Activity',
          activity_type_name: 'Conditions',
        };
        setParams(_params);
        if (conditions) {
          const onHold = conditions.results.filter(f => {
            if (f.statusName === 'Completed') {
              return false;
            }
            return f.activityTitleName === 'Inspection Hold' || f.activityTitleName === 'Permit Hold';
          });
          setDisableFromHold(onHold.length > 0);
        }
      }
    }
  }, [selectedPermit, conditions]);
  useEffect(() => {
    const _inspections = sortTableHeader(tableSortingDirection, tableSortingKey, getVisibleInspections());
    setInspections(_inspections);
  }, [tableSortingDirection, tableSortingKey]);

  useEffect(() => {
    return () => {
      dispatch(setTableSortingKey(''));
      dispatch(setTableSortingDirection('NONE'));
    };
  }, []);

  // Drop any stale selection when switching to a different permit.
  useEffect(() => {
    setSelectedIds([]);
  }, [selectedPermit?.permitId]);

  const getInspectionTokens = (token: string) => {
    if (!token) {
      return {
        description: '',
        code: '',
      };
    }
    const tokenArr = token.split(' ');
    return {
      description: token.substring(tokenArr[0].length).trim(),
      code: tokenArr[0],
    };
  };
  const getVisibleInspections = () => {
    if (selectedPermit.inspections) {
      return selectedPermit.inspections.map(insp => {
        const { code, description } = getInspectionTokens(insp.activityTypeName);
        const retValue = {
          id: insp.id,
          code,
          description,
          inspectionDate: insp.inspectionDate,
          statusName: insp.statusName,
          report: insp.report,
          inspectorName: [insp.inspectorFirstName, insp.inspectorLastName].filter(Boolean).join(' '),
        };
        if (insp.id === updatedStatusId) {
          setUpdatedStatusId(-1);
          return {
            ...retValue,
            statusName: InspectionStatuses.READY_FOR_SCHEDULING,
          };
        }
        return retValue;
      });
    }
    return [];
  };

  const isEnabled = (item: VisibleInspectionType, linkType: ScreenType): boolean => {
    const status = item.statusName;
    switch (linkType) {
      case 'View':
        const hasReport = item.report !== null;
        if (hasReport) {
          return true;
        }
        const statusCheck = ['Cancelled', 'Failed', 'Not Applicable', 'Partial Pass', 'Pass'].indexOf(status) > -1;
        return statusCheck && hasReport;
      case 'Request':
        return status === InspectionStatuses.NOT_SCHEDULED;
    }
    return false;
  };

  const getReportUrl = (item: VisibleInspectionType) => {
    if (item.report) {
      const url = decodeURIComponent(item.report.replace(/\/media\/https%3A/, 'https:/'));
      return url;
    }
    return '';
  };

  const onRequest = (item: VisibleInspectionType) => {
    const disabled = !isEnabled(item, 'Request');
    if (disabled) return;
    const fullItem = selectedPermit.inspections.find(i => i.id === item.id);
    dispatch(setSelectedInspection(fullItem));
    dispatch(setBottomDrawerOpened(true));
  };

  const selectableItems = inspections.filter(i => isEnabled(i, 'Request'));
  const selectableIds = selectableItems.map(i => i.id);
  const allSelectableSelected =
    selectableIds.length > 0 &&
    selectableIds.every(id => selectedIds.includes(id));

  const toggleRow = (id: number) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
    );
  };

  const toggleAll = () => {
    setSelectedIds(allSelectableSelected ? [] : selectableIds);
  };

  const openBulk = () => {
    const targets = (selectedPermit.inspections || []).filter(i =>
      selectedIds.includes(i.id),
    );
    if (targets.length === 0) return;
    setBulkTargets(targets);
    setBulkOpen(true);
  };

  const getStatusLabel = (item: VisibleInspectionType) => {
    if (item.statusName === InspectionStatuses.READY_FOR_SCHEDULING) {
      return 'Inspector to be assigned';
    }
    return item.statusName || '—';
  };

  const toggleSort = (key: string) => {
    if (tableSortingKey !== key) {
      dispatch(setTableSortingKey(key));
      dispatch(setTableSortingDirection('ASC'));
      return;
    }
    const next = tableSortingDirection === 'ASC' ? 'DESC' : tableSortingDirection === 'DESC' ? 'NONE' : 'ASC';
    dispatch(setTableSortingDirection(next as any));
  };

  const sortIndicator = (key: string) => {
    if (tableSortingKey !== key || tableSortingDirection === 'NONE') return '⇅';
    return tableSortingDirection === 'ASC' ? '↑' : '↓';
  };

  const isHeaderLoading =
    !selectedPermit?.namePermitName && !selectedPermit?.address;
  const isInspectionListLoading =
    !selectedPermit?.inspections || conditionsFetching;

  return (
    <Root>
      {isHeaderLoading ? (
        <LoadingCard role="status" aria-live="polite">
          <Loader size={28} thickness={4} />
          <LoadingText>Loading permit details…</LoadingText>
        </LoadingCard>
      ) : (
        <HeaderCard>
          <HeaderCol>
            <HeaderLabel>Project / Permit</HeaderLabel>
            <HeaderTitle title={selectedPermit?.namePermitName || ''}>{selectedPermit?.namePermitName || '—'}</HeaderTitle>
          </HeaderCol>
          <HeaderDivider />
          <HeaderCol>
            <HeaderLabel>Address</HeaderLabel>
            <HeaderAddress title={selectedPermit?.address || ''}>
              <PlaceIcon style={{ fontSize: 16 }} />
              <span>{selectedPermit?.address || '—'}</span>
            </HeaderAddress>
          </HeaderCol>
        </HeaderCard>
      )}

      <SectionEyebrow>Inspection List</SectionEyebrow>

      {!isInspectionListLoading && selectableItems.length > 0 && (
        <BulkBar>
          <BulkBarText>
            {selectedIds.length > 0
              ? `${selectedIds.length} selected`
              : 'Select “Not Scheduled” inspections to request them together'}
            <Tooltip
              arrow
              placement="top"
              title="Only Not Scheduled inspections can be selected. If you want to select other inspections, they need to be changed to Not Scheduled status first."
            >
              <InfoIcon>
                <InfoOutlinedIcon fontSize="inherit" />
              </InfoIcon>
            </Tooltip>
          </BulkBarText>
          <BulkBarButton
            type="button"
            disabled={selectedIds.length === 0}
            onClick={openBulk}
          >
            Request Selected
          </BulkBarButton>
        </BulkBar>
      )}

      <TableCard>
        <THead>
          <CheckTh>
            <CheckInput
              type="checkbox"
              aria-label="Select all not scheduled inspections"
              checked={allSelectableSelected}
              disabled={selectableIds.length === 0}
              onChange={toggleAll}
            />
          </CheckTh>
          <SortableTh onClick={() => toggleSort('code')}>
            <span>Code</span>
            <SortMark>{sortIndicator('code')}</SortMark>
          </SortableTh>
          <SortableTh onClick={() => toggleSort('description')}>
            <span>Description</span>
            <SortMark>{sortIndicator('description')}</SortMark>
          </SortableTh>
          <SortableTh onClick={() => toggleSort('inspectionDate')}>
            <span>Date</span>
            <SortMark>{sortIndicator('inspectionDate')}</SortMark>
          </SortableTh>
          <SortableTh onClick={() => toggleSort('statusName')}>
            <span>Status</span>
            <SortMark>{sortIndicator('statusName')}</SortMark>
          </SortableTh>
          <Th>
            <span>Inspector</span>
          </Th>
          <Th>Actions</Th>
        </THead>

        <TBody>
          {isInspectionListLoading && (
            <LoadingRow role="status" aria-live="polite">
              <Loader size={22} thickness={4} />
              <span>Loading inspections…</span>
            </LoadingRow>
          )}
          {!isInspectionListLoading && inspections.length === 0 && (
            <EmptyRow>No inspections on this permit yet.</EmptyRow>
          )}
          {!isInspectionListLoading && inspections.map((item: VisibleInspectionType) => {
            const canRequest = isEnabled(item, 'Request');
            const canView = isEnabled(item, 'View');
            const tone = toneByStatus(item.statusName);
            const pal = tonePalette[tone];
            return (
              <Tr key={item.id} $selected={selectedIds.includes(item.id)}>
                <CheckTd data-label="Select">
                  <CheckInput
                    type="checkbox"
                    aria-label={`Select inspection ${item.code}`}
                    checked={selectedIds.includes(item.id)}
                    disabled={!canRequest}
                    onChange={() => toggleRow(item.id)}
                  />
                </CheckTd>
                <Td data-label="Code">
                  <Mono title={item.code}>{item.code || '—'}</Mono>
                </Td>
                <Td data-label="Description">
                  <Ellipsis title={item.description}>{item.description || '—'}</Ellipsis>
                </Td>
                <Td data-label="Date">{item.inspectionDate || '—'}</Td>
                <Td data-label="Status">
                  <StatusChip
                    title={getStatusLabel(item)}
                    style={{ background: pal.bg, color: pal.fg, borderColor: pal.border }}
                  >
                    {getStatusLabel(item)}
                  </StatusChip>
                </Td>
                <Td data-label="Inspector">
                  {/* Clients only see who's coming on the day of the inspection. */}
                  <Ellipsis title={isToday(item.inspectionDate) ? item.inspectorName : ''}>
                    {(isToday(item.inspectionDate) && item.inspectorName) || '-'}
                  </Ellipsis>
                </Td>
                <ActionsCell>
                  <ActionBtn $variant="primary" disabled={!canRequest} onClick={() => onRequest(item)}>
                    <AssignmentLateIcon />
                    <span>Request</span>
                  </ActionBtn>
                  {canView ? (
                    <ActionLink href={getReportUrl(item)} target="_blank" rel="noreferrer">
                      <VisibilityIcon />
                      <span>View</span>
                    </ActionLink>
                  ) : (
                    <ActionBtn $variant="ghost" disabled>
                      <VisibilityIcon />
                      <span>View</span>
                    </ActionBtn>
                  )}
                </ActionsCell>
              </Tr>
            );
          })}
        </TBody>
      </TableCard>

      <EditInspectionPopup disableFromHold={disableFromHold} updateNewStatus={setUpdatedStatusId} />

      {bulkOpen && (
        <BulkRequestInspectionsPopup
          selectedInspections={bulkTargets}
          disableFromHold={disableFromHold}
          onClose={() => setBulkOpen(false)}
          onSuccess={() => setSelectedIds([])}
        />
      )}
    </Root>
  );
};

export default PermitInspectionsDetails;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont,
    'Helvetica Neue', Arial, Roboto, sans-serif;
`;

const HeaderCard = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 1px minmax(0, 1fr);
  gap: 24px;
  align-items: stretch;
  padding: 18px 20px;
  background: var(--neutral-50);
  border: 1px solid var(--border);
  border-radius: 10px;

  @media only screen and (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 14px;
  }
`;

const HeaderCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const HeaderDivider = styled.div`
  background: var(--border);
  width: 1px;

  @media only screen and (max-width: 640px) {
    display: none;
  }
`;

const HeaderLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const HeaderTitle = styled.span`
  font-size: 1.0625rem;
  font-weight: 700;
  color: var(--mainText);
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HeaderAddress = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9375rem;
  color: var(--neutral-700);
  line-height: 1.35;
  min-width: 0;

  svg {
    color: var(--neutral-500);
    flex-shrink: 0;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const SectionEyebrow = styled.div`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
  padding: 0 2px;
`;

const BulkBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 10px 14px;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  box-sizing: border-box;
`;

const BulkBarText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--neutral-600);
`;

const InfoIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: var(--neutral-400);
  font-size: 18px;
  cursor: help;
  transition: color 0.15s ease;

  &:hover {
    color: var(--primary-600);
  }
`;

const BulkBarButton = styled.button`
  min-width: 150px;
  height: 38px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  background: var(--primary-600);
  color: var(--neutral-0);
  font-weight: 600;
  font-size: 0.875rem;
  letter-spacing: 0.01em;
  text-transform: none;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--primary-700);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
  &:disabled {
    background: var(--neutral-200);
    color: var(--neutral-400);
    cursor: not-allowed;
  }
`;

const CheckTh = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 8px;
`;

const CheckTd = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 8px;

  @media only screen and (max-width: 840px) {
    justify-content: flex-start;
    padding: 6px 0;

    &::before {
      content: attr(data-label);
      font-size: 0.7rem;
      font-weight: 700;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      color: var(--neutral-500);
      min-width: 84px;
    }
  }
`;

const CheckInput = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: var(--primary-600);

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

// Fluid columns: every track can shrink, so the table always fits the modal
// (fixed widths used to squeeze Description/Code to zero on narrower screens).
const GRID_COLUMNS =
  '40px minmax(64px, 0.7fr) minmax(0, 1.6fr) minmax(100px, 0.9fr) minmax(0, 1.1fr) minmax(0, 1fr) 112px';

const TableCard = styled.div`
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  overflow: hidden;
`;

const THead = styled.div`
  display: grid;
  grid-template-columns: ${GRID_COLUMNS};
  background: var(--neutral-100);
  border-bottom: 1px solid var(--border);


  @media only screen and (max-width: 840px) {
    display: none;
  }
`;

const Th = styled.div`
  padding: 12px 10px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--neutral-500);
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  overflow: hidden;

  > span:first-child {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }
`;

const SortableTh = styled(Th)`
  cursor: pointer;
  user-select: none;
  transition: color 0.15s ease;

  &:hover {
    color: var(--primary-600);
  }
`;

const SortMark = styled.span`
  font-size: 12px;
  color: var(--neutral-400);

  ${SortableTh}:hover & {
    color: var(--primary-600);
  }
`;

const TBody = styled.div`
  max-height: 52vh;
  overflow-y: auto;
`;

const Tr = styled.div<{ $selected?: boolean }>`
  display: grid;
  grid-template-columns: ${GRID_COLUMNS};
  border-bottom: 1px solid var(--border);
  transition: background-color 0.15s ease;
  align-items: center;
  background: ${({ $selected }) => ($selected ? 'var(--primary-50)' : 'transparent')};

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background: ${({ $selected }) =>
      $selected ? 'var(--primary-100)' : 'var(--neutral-50)'};
  }


  @media only screen and (max-width: 840px) {
    grid-template-columns: 1fr;
    padding: 8px 12px;
  }
`;

const Td = styled.div`
  padding: 12px 10px;
  font-size: 0.875rem;
  color: var(--mainText);
  min-width: 0;

  @media only screen and (max-width: 840px) {
    padding: 6px 0;
    display: flex;
    align-items: baseline;
    gap: 6px;

    &::before {
      content: attr(data-label);
      font-size: 0.7rem;
      font-weight: 700;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      color: var(--neutral-500);
      min-width: 84px;
    }
  }
`;

const Ellipsis = styled.span`
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Mono = styled.span`
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 0.8125rem;
  color: var(--neutral-700);
  background: var(--neutral-100);
  border: 1px solid var(--border);
  padding: 2px 8px;
  border-radius: 6px;
  display: inline-block;
  max-width: 100%;
  box-sizing: border-box;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
`;

const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid var(--border);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  line-height: 1.4;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ActionsCell = styled.div`
  padding: 12px 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: stretch;

  @media only screen and (max-width: 840px) {
    padding: 8px 0;
    flex-direction: row;
    align-items: center;
  }
`;

const actionBaseStyles = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;

  svg {
    font-size: 16px;
  }
`;

const ActionBtn = styled.button<{ $variant: 'primary' | 'ghost' }>`
  ${actionBaseStyles}
  border: 1px solid ${({ $variant }) => ($variant === 'primary' ? 'transparent' : 'var(--border)')};
  background: ${({ $variant }) => ($variant === 'primary' ? 'var(--primary-600)' : 'var(--neutral-0)')};
  color: ${({ $variant }) => ($variant === 'primary' ? 'var(--neutral-0)' : 'var(--neutral-700)')};

  &:hover:not(:disabled) {
    background: ${({ $variant }) => ($variant === 'primary' ? 'var(--primary-700)' : 'var(--neutral-50)')};
    border-color: ${({ $variant }) => ($variant === 'primary' ? 'transparent' : 'var(--neutral-400)')};
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const ActionLink = styled.a`
  ${actionBaseStyles}
  border: 1px solid var(--border);
  background: var(--neutral-0);
  color: var(--neutral-700);

  &:hover {
    background: var(--primary-600);
    color: var(--neutral-0);
    border-color: var(--primary-600);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const EmptyRow = styled.div`
  padding: 40px 16px;
  text-align: center;
  color: var(--neutral-500);
  font-size: 0.875rem;
`;

const LoadingCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 120px;
  padding: 24px;
  background: var(--neutral-50);
  border: 1px solid var(--border);
  border-radius: 10px;
`;

const LoadingText = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--neutral-600);
  letter-spacing: 0.01em;
`;

const LoadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 32px 16px;
  font-size: 0.875rem;
  color: var(--neutral-600);
  font-weight: 500;
`;
